import { HashLink } from 'react-router-hash-link';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth, useApi } from '../context/AuthContext';

export default function Navbar() {
  const { isLoggedIn, user, login, logout, loading, displayName } = useAuth();
  const api = useApi();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const dropdownRef = useRef(null);

  // Check if the logged-in user is a fleet admin
  const isAdmin = Array.isArray(user?.profile?.["cognito:groups"]) &&
    user.profile["cognito:groups"].includes("fleet-admins");

  // Check verification status when logged in
  useEffect(() => {
    if (!isLoggedIn) {
      setIsVerified(false);
      return;
    }
    api.get('/users/me/verify/status')
      .then(r => setIsVerified(r.data?.verificationStatus === 'verified'))
      .catch(() => setIsVerified(false));
  }, [isLoggedIn]);

  // Function to handle clicks outside the dropdown/button area
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The Cognito Signup URL used in both menus
  const signupUrl = `${process.env.REACT_APP_COGNITO_AUTH_URL}/signup?` +
    `client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&` +
    `response_type=code&` +
    `scope=email+openid+profile&` +
    `redirect_uri=${process.env.REACT_APP_REDIRECT_URI}`;

  return (
    <nav className="bg-white shadow-lg sticky top-0 z-50 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <i data-feather="car" className="h-8 w-8 text-blue-600"></i>
            <a href="/" className="flex items-center">
              <img
                src="/drive-dobias-logo.png"
                alt="Company Logo"
                className="h-12 w-auto mr-2"
              />
              <span className="font-bold text-xl text-brandBlue">Drive Dobias</span>
            </a>
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-6">
            <HashLink smooth to="/#vehicles" className="text-gray-600 hover:text-gray-900">Our Fleet</HashLink>
            <HashLink smooth to="/#how-it-works" className="text-gray-600 hover:text-gray-900">How It Works</HashLink>
            <HashLink smooth to="/#faq" className="text-gray-600 hover:text-gray-900">FAQ</HashLink>
            <HashLink smooth to="/#contact" className="text-gray-600 hover:text-gray-900">Contact</HashLink>

            {/* Book Now / Private Rentals — depends on verification */}
            {isVerified ? (
              <Link
                to="/private-rentals"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Book Now
              </Link>
            ) : (
              <HashLink smooth to="/#contact" className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
                Book Now
              </HashLink>
            )}

            {/* Desktop Account Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100"
              >
                {isLoggedIn ? (
                  <span>{`Hi, ${displayName}`}</span>
                ) : (
                  // Person icon SVG (from Heroicons)
                  <svg xmlns="www.w3.org" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}

                {/* Chevron SVG Icon */}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20 border border-gray-200">
                  {isLoggedIn ? (
                    <>
                      <div className="px-4 py-2 text-sm text-gray-700 font-semibold truncate border-b">
                        {displayName}
                      </div>
                      <HashLink to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Profile
                      </HashLink>
                      {!isVerified && (
                        <Link
                          to="/verify"
                          onClick={() => setIsDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                        >
                          🔒 Get Verified
                        </Link>
                      )}
                      {isAdmin && (
                        <a
                          href="/admin"
                          onClick={() => setIsDropdownOpen(false)}
                          className="block px-4 py-2 text-sm text-blue-700 font-medium hover:bg-blue-50 border-t border-gray-100"
                        >
                          ⚙️ Admin Panel
                        </a>
                      )}
                      <button
                        onClick={() => {
                          logout();
                          setIsDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          login(); // Triggers Cognito Hosted UI redirect
                          setIsDropdownOpen(false);
                        }}
                        disabled={loading}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {loading ? 'Loading...' : 'Login'}
                      </button>
                      <a
                        href={signupUrl}
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Register
                      </a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileOpen(true)}
              className="text-gray-700 focus:outline-none"
            >
              ☰
            </button>
          </div>

        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="relative w-64 bg-white shadow-lg p-6">
            <button
              className="absolute top-2 right-2 text-gray-600"
              onClick={() => setMobileOpen(false)}
            >
              ✕
            </button>

            {/* Mobile Navigation Links */}
            <nav className="flex flex-col space-y-4 mt-8">

              {/* === MOBILE LOGIN/LOGOUT SECTION === */}
              <div className="border-b pb-4 mb-4">
                {isLoggedIn ? (
                  // Logged In: Show Cognito user info
                  <>
                    <span className="block px-4 py-2 text-sm text-gray-700 font-bold truncate">
                      Hi, {displayName}
                    </span>

                    <HashLink
                      to="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Profile
                    </HashLink>

                    {!isVerified && (
                      <Link
                        to="/verify"
                        onClick={() => setMobileOpen(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                      >
                        🔒 Get Verified
                      </Link>
                    )}

                    {isAdmin && (
                      <a
                        href="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-blue-700 font-medium hover:bg-blue-50"
                      >
                        ⚙️ Admin Panel
                      </a>
                    )}

                    <button
                      onClick={() => {
                        logout(); // From AuthContext
                        setMobileOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 font-medium hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  // Not Logged In: Show Cognito Login/Register
                  <>
                    <button
                      onClick={() => {
                        login(); // Redirects to Cognito Hosted UI
                        setMobileOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 font-semibold hover:bg-gray-100"
                    >
                      Login
                    </button>
                    <a
                      href={signupUrl}
                      className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                    >
                      Register
                    </a>
                  </>
                )}
              </div>
              {/* === END MOBILE LOGIN/LOGOUT SECTION === */}

              {/* Existing navigation links */}
              <HashLink smooth to="/#vehicles" onClick={() => setMobileOpen(false)} className="px-4 py-2">Our Fleet</HashLink>
              <HashLink smooth to="/#how-it-works" onClick={() => setMobileOpen(false)} className="px-4 py-2">How It Works</HashLink>
              <HashLink smooth to="/#faq" onClick={() => setMobileOpen(false)} className="px-4 py-2">FAQ</HashLink>
              <HashLink smooth to="/#contact" onClick={() => setMobileOpen(false)} className="px-4 py-2">Contact</HashLink>

              {/* Book Now / Private Rentals — depends on verification */}
              {isVerified ? (
                <Link
                  to="/private-rentals"
                  onClick={() => setMobileOpen(false)}
                  className="mx-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-center"
                >
                  Book Now
                </Link>
              ) : (
                <HashLink
                  smooth
                  to="/#contact"
                  onClick={() => setMobileOpen(false)}
                  className="mx-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-center"
                >
                  Book Now
                </HashLink>
              )}
            </nav>
          </div>
        </div>
      )}
    </nav>
  );
}
